import { useMemo } from "preact/hooks";
import {
  buildFocusContextKey,
  createSlashToolbarBindings,
  useSlashActionToolbar
} from "../components/FloatingActionToolbar/useSlashActionToolbar.js";
import { useEscapeStack } from "../lib/shortcuts/useEscapeStack.js";
import { handlePageEscapeReset, hasPageEscapeResetAction } from "../lib/shortcuts/pageEscapeReset.js";
import {
  setAnalysisChartCategory,
  setAnalysisChartPaymentMethod,
  setAnalysisChartService,
  setAnalysisChartSupplier,
  setBudgetSearchQuery
} from "../lib/pageSearchState.js";
import { createSectionNavigationBindings } from "../lib/shortcuts/createSectionNavigationBindings.js";

function getExpenseToolbarSelectedText(selectedCellState) {
  const selectedColumnKey = selectedCellState?.column?.key ?? "";
  const selectedRow = selectedCellState?.row;
  const resolvers = {
    categoryId: () => selectedCellState?.row?.category,
    dateMonth: () => (selectedRow ? selectedCellState?.column?.displayValue?.(selectedRow) : ""),
    method: () => selectedCellState?.row?.method,
    type: () => (selectedRow ? selectedCellState?.column?.displayValue?.(selectedRow) : ""),
    supplier: () => selectedCellState?.row?.supplier,
    service: () => selectedCellState?.row?.service
  };

  return String(resolvers[selectedColumnKey]?.() ?? "").trim();
}

function buildExpenseToolbarActions({
  gridApiRef,
  onFilterRequest,
  onNavigateToPage,
  selectedCellText,
  selectedColumnKey,
  t
}) {
  if (selectedColumnKey === "categoryId") {
    return [
      {
        key: "search-expenses",
        label: t("filter"),
        onSelect: () => {
          onFilterRequest?.(selectedCellText);
          gridApiRef.current?.focusGrid?.();
        }
      },
      {
        key: "show-in-budget",
        label: t("budget"),
        onSelect: () => {
          setBudgetSearchQuery(selectedCellText);
          onNavigateToPage?.("budget");
        }
      },
      {
        key: "show-in-chart",
        label: t("chart"),
        onSelect: () => {
          setAnalysisChartCategory(selectedCellText);
          onNavigateToPage?.("analysis", "charts");
        }
      }
    ];
  }

  if (selectedColumnKey === "supplier") {
    return [
      {
        key: "search-expenses",
        label: t("filter"),
        onSelect: () => {
          onFilterRequest?.(selectedCellText);
          gridApiRef.current?.focusGrid?.();
        }
      },
      {
        key: "show-in-chart",
        label: t("chart"),
        onSelect: () => {
          setAnalysisChartSupplier(selectedCellText);
          onNavigateToPage?.("analysis", "charts");
        }
      }
    ];
  }

  if (selectedColumnKey === "service") {
    return [
      {
        key: "search-expenses",
        label: t("filter"),
        onSelect: () => {
          onFilterRequest?.(selectedCellText);
          gridApiRef.current?.focusGrid?.();
        }
      },
      {
        key: "show-in-chart",
        label: t("chart"),
        onSelect: () => {
          setAnalysisChartService(selectedCellText);
          onNavigateToPage?.("analysis", "charts");
        }
      }
    ];
  }

  if (selectedColumnKey === "method") {
    return [
      {
        key: "search-expenses",
        label: t("filter"),
        onSelect: () => {
          onFilterRequest?.(selectedCellText);
          gridApiRef.current?.focusGrid?.();
        }
      },
      {
        key: "show-in-chart",
        label: t("chart"),
        onSelect: () => {
          setAnalysisChartPaymentMethod(selectedCellText);
          onNavigateToPage?.("analysis", "charts");
        }
      }
    ];
  }

  if (["dateMonth", "type"].includes(selectedColumnKey)) {
    return [
      {
        key: "search-expenses",
        label: t("filter"),
        onSelect: () => {
          onFilterRequest?.(selectedCellText);
          gridApiRef.current?.focusGrid?.();
        }
      }
    ];
  }

  return [];
}

export function useExpenseContextToolbar({
  focusContext,
  focusMainArea,
  gridApiRef,
  onFilterRequest,
  onNavigateToPage,
  searchInputRef,
  searchQuery,
  selectedCellState,
  setSearchQuery,
  setSortState,
  sortState,
  t,
  visibilitySelectRef
}) {
  const selectedColumnKey = selectedCellState?.column?.key ?? "";
  const selectedCellText = getExpenseToolbarSelectedText(selectedCellState);
  const focusContextKey = buildFocusContextKey(focusContext);
  const canOpenContextToolbar = Boolean(
    selectedCellState
    && focusContext?.focused
    && !selectedCellState.editing
    && ["categoryId", "dateMonth", "method", "type", "supplier", "service"].includes(selectedColumnKey)
    && selectedCellText
  );
  const toolbarActions = useMemo(() => buildExpenseToolbarActions({
    gridApiRef,
    onFilterRequest,
    onNavigateToPage,
    selectedCellText,
    selectedColumnKey,
    t
  }), [gridApiRef, onFilterRequest, onNavigateToPage, selectedCellText, selectedColumnKey, t]);
  const {
    setSelectedIndex,
    setToolbarOpen,
    toolbarActions: renderedToolbarActions,
    toolbarOpen
  } = useSlashActionToolbar({
    canOpen: canOpenContextToolbar,
    actions: toolbarActions,
    focusContextKey,
    gridFocused: Boolean(focusContext?.focused)
  });
  const hasEscapeAction = hasPageEscapeResetAction({
    searchQuery,
    sortState
  });

  useEscapeStack(() => handlePageEscapeReset({
    searchQuery,
    setSearchQuery,
    sortState,
    setSortState,
    onResetApplied: focusMainArea
  }), hasEscapeAction, -100);

  const pageBindings = useMemo(() => createSlashToolbarBindings({
    canOpen: canOpenContextToolbar,
    focusMainArea,
    focusPrimaryControl: () => {
      searchInputRef.current?.focus();
      searchInputRef.current?.select?.();
    },
    focusSecondaryControl: () => {
      visibilitySelectRef.current?.focus();
    },
    openToolbar: () => {
      setSelectedIndex(0);
      setToolbarOpen(true);
    },
    startDraftRowEdit: () => {
      gridApiRef.current?.startDraftRowEdit();
    }
  }), [canOpenContextToolbar, focusMainArea, gridApiRef, searchInputRef, setSelectedIndex, setToolbarOpen, visibilitySelectRef]);

  return {
    canOpenContextToolbar,
    pageBindings,
    renderedToolbarActions,
    toolbarActions,
    toolbarOpen,
    setSelectedIndex,
    setToolbarOpen
  };
}

export function createExpenseVisibilityFilterBindings(localizedVisibilityOptions, visibilityFilter, onVisibilityFilterChange) {
  return createSectionNavigationBindings({
    options: localizedVisibilityOptions,
    value: visibilityFilter,
    onChange: onVisibilityFilterChange,
    allowInInput: true
  });
}
